<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useQueryClient } from '@tanstack/vue-query';
import Button from 'primevue/button';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import { getAccountGeofenceRemovalSuggestion } from '@gpx-spa/queries';
import * as events from '@/events';
import { buildRationale, formatDays, formatLastActivity } from './formatters';
import RemovalConfirmDialog from './RemovalConfirmDialog.vue';

const props = defineProps<{
  suggestionId: string;
  rank: number;
}>();

const emit = defineEmits<{ (e: 'dismiss', id: string): void }>();

const store = useStore();
const toast = useToast();
const queryClient = useQueryClient();

const accountId = computed<number | null>(
  () => store.getters['auth/accountId'] ?? null,
);

const suggestionIdRef = computed(() => props.suggestionId);

const { suggestion, isLoading, isError } = getAccountGeofenceRemovalSuggestion(
  accountId,
  suggestionIdRef,
);

const displayName = computed(
  () => suggestion.value?.geofence_name?.trim() || 'Geofence',
);
const address = computed(
  () => suggestion.value?.geofence_address?.trim() || null,
);

const rationale = computed(() =>
  suggestion.value ? buildRationale(suggestion.value) : '',
);

const lastActivity = computed(() =>
  suggestion.value ? formatLastActivity(suggestion.value.last_activity_t) : '',
);
const daysInactive = computed(() =>
  suggestion.value ? formatDays(suggestion.value.days_since_last_activity) : '',
);
const age = computed(() =>
  suggestion.value ? formatDays(suggestion.value.geofence_age_days) : '',
);

const confirmVisible = ref(false);
const removing = ref(false);
const removed = ref(false);

function onRemoveClick() {
  if (removing.value || removed.value) return;
  confirmVisible.value = true;
}

function onConfirmRemove() {
  const s = suggestion.value;
  const acctId = accountId.value;
  if (!s || acctId == null) return;

  removing.value = true;
  store
    .dispatch('geofence/delete', { accountId: acctId, id: s.geofence_id })
    .then(() => {
      removed.value = true;
      confirmVisible.value = false;
      queryClient.invalidateQueries({
        queryKey: ['geofence-removal-suggestions', acctId],
      });
      queryClient.invalidateQueries({ queryKey: ['account-geofences'] });
      toast.success('Geofence removed.');
    })
    .catch(() => {
      toast.error('Could not remove the geofence. Please try again.');
    })
    .finally(() => {
      removing.value = false;
    });
}

function onKeep() {
  emit('dismiss', props.suggestionId);
}

// Clicking the card focuses the map on the geofence it suggests removing.
// DeviceMap (mounted elsewhere on the Trackers page) listens for this event,
// turns geofence rendering on if it's off, and zooms to the geofence.
function onFocusGeofence() {
  const geofenceId = suggestion.value?.geofence_id;
  if (geofenceId == null) return;
  events.trigger(events.names.UI_ACTION_FOCUS_GEOFENCE, {
    geofence_id: geofenceId,
  });
}
</script>

<template>
  <div
    v-if="!isLoading && !isError && suggestion"
    class="rounded-md border border-gpx_black-100/10 bg-white p-3"
    data-testid="removal-suggestion-card"
  >
    <div
      role="button"
      tabindex="0"
      class="-m-1 cursor-pointer rounded p-1 hover:bg-gpx_black-100/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f39c12]"
      title="Show this geofence on the map"
      data-testid="removal-card-focus"
      @click="onFocusGeofence"
      @keydown.enter.prevent="onFocusGeofence"
      @keydown.space.prevent="onFocusGeofence"
    >
      <div class="flex items-start gap-3">
        <div
          class="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#f39c12] text-gpx_sm font-semibold text-white"
          data-testid="removal-card-rank"
        >
          {{ props.rank }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-gpx_base font-semibold text-gpx_black-50">
            {{ displayName }}
          </p>
          <p v-if="address" class="truncate text-gpx_xs text-gpx_black-500">
            {{ address }}
          </p>
        </div>
      </div>
      <p class="mt-2 text-gpx_sm text-gpx_black-50">{{ rationale }}</p>
      <div
        class="mt-3 grid grid-cols-3 gap-2 border-t border-gpx_black-100/10 pt-3"
      >
        <div>
          <p class="text-gpx_sm font-semibold text-gpx_black-50">
            {{ lastActivity }}
          </p>
          <p class="text-gpx_xs text-gpx_black-500">Last activity</p>
        </div>
        <div>
          <p class="text-gpx_sm font-semibold text-gpx_black-50">
            {{ daysInactive }}
          </p>
          <p class="text-gpx_xs text-gpx_black-500">Days inactive</p>
        </div>
        <div>
          <p class="text-gpx_sm font-semibold text-gpx_black-50">{{ age }}</p>
          <p class="text-gpx_xs text-gpx_black-500">Age</p>
        </div>
      </div>
    </div>
    <div
      v-if="removed"
      class="mt-3 flex items-center gap-1.5 text-gpx_sm font-medium text-gpx_green-100"
      data-testid="removal-card-completed"
    >
      <CheckCircleIcon class="size-4 shrink-0" />
      <span>Geofence removed</span>
    </div>
    <div v-else class="mt-3 flex gap-2">
      <Button
        label="Remove Geofence"
        severity="danger"
        size="small"
        class="flex-1"
        :loading="removing"
        data-testid="removal-card-remove"
        @click="onRemoveClick"
      />
      <Button
        label="Keep"
        severity="secondary"
        outlined
        size="small"
        data-testid="removal-card-keep"
        @click="onKeep"
      />
    </div>
    <RemovalConfirmDialog
      v-model:visible="confirmVisible"
      :geofence-name="displayName"
      :loading="removing"
      @confirm="onConfirmRemove"
    />
  </div>
</template>
